import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close'; // Import Close Icon
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/system';
import React, { useEffect, useMemo, useState } from 'react';

interface WolfDropdownProps {
  label: string;
  items: { icon?: React.ReactNode; endingIcon?: React.ReactNode; text: string; disabled?: boolean; value: string }[];
  showBorder?: boolean;
  initialSelectedItems?: string[];
  singleSelect?: boolean;
  showClearButton?: boolean; // New flag for showing/hiding the clear button
  className?: string; // Optional className for custom styling
  disabled?: boolean; // Flag to disable the entire dropdown
  onChange: (selectedItems: DropdownSelectedItem[]) => void;
}

export interface DropdownSelectedItem {
  icon?: React.ReactNode;
  endingIcon?: React.ReactNode;
  text: string;
  disabled?: boolean;
  value: string;
}

const StyledMenu = styled(Menu)(({ theme }: { theme: any }) => ({
  '& .MuiPaper-root': {
    minWidth: '200px',
    maxHeight: '300px',
    boxShadow: theme.shadows[1],
    border: 0,
  },
  '& .MuiMenuItem-root': {
    padding: theme.spacing(2),
    '&:hover': {
      background: theme.palette.frozenGrayShades2.main,
    },
  },
  '& .MuiList-root': {
    padding: 0,
  },
  '& .MuiListItemText-root': {
    fontSize: '16px',
  },
}));

const WolfDropdown: React.FC<WolfDropdownProps> = ({
  label,
  items,
  showBorder = false,
  singleSelect = false,
  initialSelectedItems = [],
  showClearButton = false, // Default value for showClearButton is false
  className = '', // Default to empty string
  disabled = false, // Default value for disabled is false
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Use useMemo to memoize the filtered items
  const filteredItems = useMemo(
    () => items.filter((item: DropdownSelectedItem) => initialSelectedItems.includes(item.value)),
    [items, initialSelectedItems],
  );

  const [selectedItems, setSelectedItems] = useState<DropdownSelectedItem[]>(filteredItems);

  useEffect(() => {
    // Only update if the filtered items are different from current selection
    const hasChanged =
      filteredItems.length !== selectedItems.length ||
      filteredItems.some((item, index) => item.value !== selectedItems[index]?.value);

    if (hasChanged) {
      setSelectedItems(filteredItems);
    }
  }, [filteredItems, selectedItems]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (item: {
    icon?: React.ReactNode;
    endingIcon?: React.ReactNode;
    text: string;
    disabled?: boolean;
    value: string;
  }) => {
    if (singleSelect) {
      setSelectedItems([item]);
      onChange([item]);
      handleClose();
      return;
    }
    const isSelected = selectedItems.some((el) => el.value === item.value);

    const updatedSelectedItems = isSelected
      ? selectedItems.filter((el) => el.value !== item.value)
      : [...selectedItems, item];

    onChange(updatedSelectedItems);
    setSelectedItems(updatedSelectedItems);
    handleClose();
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
    onChange([]);
  };

  return (
    <Box className="w-full">
      <Button
        variant="outlined"
        className={`!bg-white !justify-between !text-body16 !w-full !border-frozenGrayShades2 ${className}`}
        sx={{
          textTransform: 'none',
          border: showBorder ? 2 : 1,
          borderColor: showBorder ? undefined : 'transparent',
          '&:hover': {
            border: showBorder ? 2 : 1,
            borderColor: showBorder ? undefined : 'transparent',
          }
        }}
        endIcon={
          <>
            {showClearButton && selectedItems.length > 0 && (
              <Box className="!p-0 text-additionalBorder w-6 h-6" onClick={handleClearSelection}>
                <CloseIcon />
              </Box>
            )}
            <ArrowDropDownIcon />
          </>
        }
        disabled={disabled}
        onClick={handleClick}>
        {label || ' '}
      </Button>
      <StyledMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {items.map((item, index) => (
          <MenuItem
            key={index}
            disabled={item.disabled}
            onClick={() => handleMenuItemClick(item)}
            selected={selectedItems.some((selectedItem) => selectedItem.value === item.value)}>
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.text} disableTypography={true} className={className} />
            {item.endingIcon && <ListItemIcon>{item.endingIcon}</ListItemIcon>}
          </MenuItem>
        ))}
      </StyledMenu>
    </Box>
  );
};

export default WolfDropdown;
